import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import * as turf from '@turf/turf'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { FormControl, FormHelperText } from '@mui/material'
import CoordinatesInput from '../CoordinatesInputField/CoordinatesInputField'
import { GeoJSON } from '../../utils/GeoJSON/index'
import { useUserRole } from '../../hooks/useUserRole'
import { findManualSiteStratum } from './manualSiteStratum'

interface FormData {
  lat: number | null
  lng: number | null
  actualLocation: [number, number] | null
  name: string
}

interface AddManualSiteFormModalProps {
  monitoringSitesBySamplingAreas: Record<string, turf.Feature[]>
  onClose: () => void
  onSubmit: (data: FormData) => void
  open: boolean
  selectedStrata?: turf.Feature
  stratas: turf.FeatureCollection
  initialCoordinates?: [number | null, number | null]
}

const FORM_DEFAULT_VALUES: FormData = {
  lat: null,
  lng: null,
  actualLocation: null,
  name: '',
}

const AddManualSiteFormModal: FC<AddManualSiteFormModalProps> = ({
  monitoringSitesBySamplingAreas,
  onClose,
  onSubmit,
  open,
  selectedStrata,
  stratas,
  initialCoordinates,
}) => {
  const { isViewOnly, isAdmin } = useUserRole()
  const form = useForm<FormData>({
    shouldFocusError: true,
    shouldUseNativeValidation: false,
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    shouldUnregister: true,
    mode: 'onTouched',
    defaultValues: {
      lat:
        initialCoordinates?.[0] !== null && initialCoordinates?.[0] !== undefined
          ? Number(initialCoordinates[0])
          : null,
      lng:
        initialCoordinates?.[1] !== null && initialCoordinates?.[1] !== undefined
          ? Number(initialCoordinates[1])
          : null,
      actualLocation:
        initialCoordinates?.[0] !== null &&
        initialCoordinates?.[1] !== null &&
        initialCoordinates?.[0] !== undefined &&
        initialCoordinates?.[1] !== undefined
          ? [Number(initialCoordinates[0]), Number(initialCoordinates[1])]
          : null,
      name: '',
    },
  })

  // Precargar coordenadas iniciales cuando se abra el modal o cambien las coordenadas iniciales
  useEffect(() => {
    if (
      open &&
      initialCoordinates &&
      initialCoordinates.length === 2 &&
      initialCoordinates[0] !== null &&
      initialCoordinates[1] !== null
    ) {
      const [lat, lng] = initialCoordinates
      const latNum = Number(lat)
      const lngNum = Number(lng)

      // Verificar que sean números válidos
      if (!Number.isNaN(latNum) && !Number.isNaN(lngNum)) {
        // Setear lat y lng como números (valueAsNumber los espera así)
        form.setValue('lat', latNum, {
          shouldTouch: true,
          shouldValidate: true,
          shouldDirty: true,
        })
        form.setValue('lng', lngNum, {
          shouldTouch: true,
          shouldValidate: true,
          shouldDirty: true,
        })
        // actualLocation como array de números [lat, lng]
        form.setValue('actualLocation', [latNum, lngNum], {
          shouldTouch: true,
          shouldValidate: true,
          shouldDirty: true,
        })
      }
    } else if (open) {
      // Si se abre sin coordenadas iniciales válidas, resetear el formulario
      form.reset(FORM_DEFAULT_VALUES)
    }
  }, [open, initialCoordinates, form])

  const handleClose = () => {
    form.reset(FORM_DEFAULT_VALUES)
    onClose()
  }

  const handleSubmit = (data: FormData) => {
    onSubmit(data)
    form.reset(FORM_DEFAULT_VALUES)
    onClose()
  }

  const coordinates = form.watch('actualLocation')

  // Función para generar nombre sugerido
  const getSuggestedSiteName = useCallback(
    (strataName: string): string => {
      const sites = monitoringSitesBySamplingAreas[strataName]
      if (!sites || sites.length === 0) {
        return `${strataName}-1`
      }
      let maxNumber = 0
      sites.forEach(site => {
        const match = site.properties?.name?.match(/(\d+)$/)
        if (match) {
          const num = parseInt(match[1], 10)
          if (!Number.isNaN(num) && num > maxNumber) {
            maxNumber = num
          }
        }
      })
      return `${strataName}-${maxNumber + 1}`
    },
    [monitoringSitesBySamplingAreas],
  )

  // Función para validar distancia mínima (15 metros) — admins bypass this restriction
  const checkMinimumDistance = useCallback(
    (point: turf.Feature): { isValid: boolean; minDistance: number } => {
      if (isAdmin) return { isValid: true, minDistance: 0 }

      const MIN_DISTANCE_METERS = 15
      let minDistance = Infinity

      // Si hay un selectedStrata, validar solo contra sitios de ese estrato
      if (selectedStrata && selectedStrata.properties.name) {
        const strataSites = monitoringSitesBySamplingAreas[selectedStrata.properties.name] || []
        strataSites.forEach(site => {
          const distance = GeoJSON.distance(point, site)
          if (distance < minDistance) {
            minDistance = distance
          }
        })
      } else {
        // Si no hay selectedStrata, validar contra todos los sitios (comportamiento original)
        Object.values(monitoringSitesBySamplingAreas).forEach(sites => {
          sites.forEach(site => {
            const distance = GeoJSON.distance(point, site)
            if (distance < minDistance) {
              minDistance = distance
            }
          })
        })
      }

      return {
        isValid: minDistance >= MIN_DISTANCE_METERS,
        minDistance: minDistance === Infinity ? 0 : minDistance,
      }
    },
    [isAdmin, monitoringSitesBySamplingAreas, selectedStrata],
  )

  const foundStrata = useMemo(() => {
    if (!coordinates || !Array.isArray(coordinates) || coordinates.length !== 2) {
      return null
    }

    const [lat, lng] = coordinates

    // Validar que las coordenadas sean números válidos
    if (
      typeof lat !== 'number' ||
      typeof lng !== 'number' ||
      Number.isNaN(lat) ||
      Number.isNaN(lng)
    ) {
      return null
    }

    // Turf usa [longitude, latitude]
    return findManualSiteStratum({
      point: turf.point([lng, lat]),
      manualSiteStrata: stratas,
      selectedStrataId: selectedStrata?.properties?.id,
    })
  }, [stratas, coordinates, selectedStrata])

  // Validar distancia cuando hay coordenadas y samplingArea
  const distanceValidation = useMemo(() => {
    if (!coordinates || !Array.isArray(coordinates) || coordinates.length !== 2 || !foundStrata) {
      return { isValid: true, minDistance: 0 }
    }

    const [lat, lng] = coordinates

    // Validar que las coordenadas sean números válidos
    if (
      typeof lat !== 'number' ||
      typeof lng !== 'number' ||
      Number.isNaN(lat) ||
      Number.isNaN(lng)
    ) {
      return { isValid: true, minDistance: 0 }
    }

    // Turf usa [longitude, latitude]
    const point = turf.point([lng, lat])
    return checkMinimumDistance(point)
  }, [coordinates, foundStrata, checkMinimumDistance])

  // Extraer todos los nombres de sitios existentes a nivel de farm
  const allExistingSiteNames = useMemo(() => {
    const names = new Set<string>()
    Object.values(monitoringSitesBySamplingAreas).forEach(sites => {
      sites.forEach(site => {
        if (site.properties?.name) {
          names.add(site.properties.name)
        }
      })
    })
    return names
  }, [monitoringSitesBySamplingAreas])

  // Actualizar nombre sugerido cuando cambia el samplingArea
  useEffect(() => {
    const strataToUse = selectedStrata || foundStrata
    if (strataToUse && !form.getValues('name')) {
      const suggestedName = getSuggestedSiteName(strataToUse.properties.name)
      form.setValue('name', suggestedName)
    }
  }, [foundStrata, selectedStrata, form, getSuggestedSiteName])

  // Determinar si el botón debe estar deshabilitado
  const isSubmitDisabled =
    isViewOnly ||
    !coordinates ||
    !Array.isArray(coordinates) ||
    coordinates.length !== 2 ||
    !foundStrata ||
    !distanceValidation.isValid ||
    !!form.formState.errors.name

  // Generar placeholder dinámico basado en el estrato encontrado
  const namePlaceholder = foundStrata ? `Ej: ${foundStrata.properties.name}-1` : 'Ej: Estrato-A-1'

  // Reglas de validación para el nombre del sitio (se actualizan cuando cambia allExistingSiteNames)
  const nameValidationRules = useMemo(() => {
    return {
      required: 'El nombre del sitio es requerido',
      validate: (value: string) => {
        if (!value || value.trim() === '') {
          return true // La validación required ya maneja esto
        }
        if (allExistingSiteNames.has(value.trim())) {
          return 'El nombre del sitio ya existe'
        }
        return true
      },
    }
  }, [allExistingSiteNames])

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>Agregar Sitio Manual</DialogTitle>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <DialogContent>
            <Stack spacing={3} sx={{ paddingTop: '8px' }}>
              {/* Warning sobre sitios manuales y muestreos SOC */}
              <Alert severity="warning">
                <Typography
                  sx={{
                    fontSize: 14,
                  }}
                >
                  Los sitios manuales no son contemplados para muestreos SOC de Programa de carbono
                </Typography>
              </Alert>

              {/* Fieldset para Coordenadas */}
              <Box
                component="fieldset"
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  padding: 2,
                  margin: 0,
                  color: 'inherit',
                }}
              >
                <Typography
                  component="legend"
                  sx={{ padding: '0 8px', fontSize: '0.875rem', fontWeight: 500 }}
                  variant="subtitle2"
                >
                  Coordenadas del Sitio
                </Typography>
                <Stack spacing={2} sx={{ marginTop: 1 }}>
                  <CoordinatesInput readOnly={false} selectedConfirmationSite={{ id: 'manual' }} />

                  {coordinates &&
                    Array.isArray(coordinates) &&
                    coordinates.length === 2 &&
                    !foundStrata && (
                      <Alert severity="error">
                        {selectedStrata
                          ? `Las coordenadas ingresadas no están dentro del estrato "${selectedStrata.properties.name}".`
                          : 'No se encontró un estrato asociado a las coordenadas ingresadas.'}
                      </Alert>
                    )}

                  {coordinates &&
                    Array.isArray(coordinates) &&
                    coordinates.length === 2 &&
                    foundStrata &&
                    !distanceValidation.isValid && (
                      <Alert severity="error">
                        El sitio está muy cerca de un sitio existente (
                        {distanceValidation.minDistance.toFixed(2)} m). La distancia mínima
                        requerida es de 15 metros.
                        {selectedStrata && (
                          <span>
                            {' '}
                            (Validación dentro del estrato{' '}
                            <strong>{selectedStrata.properties.name}</strong>)
                          </span>
                        )}
                      </Alert>
                    )}

                  {coordinates &&
                    Array.isArray(coordinates) &&
                    coordinates.length === 2 &&
                    foundStrata &&
                    distanceValidation.isValid && (
                      <Alert severity="success">
                        {selectedStrata ? (
                          <span>
                            Coordenadas válidas dentro del estrato:{' '}
                            <strong>{foundStrata.properties.name}</strong>
                          </span>
                        ) : (
                          <span>
                            Estrato encontrado: <strong>{foundStrata.properties.name}</strong>
                          </span>
                        )}
                      </Alert>
                    )}
                </Stack>
              </Box>

              <Divider />

              {/* Fieldset para Información del Sitio */}
              <Box
                component="fieldset"
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  padding: 2,
                  margin: 0,
                  color: 'inherit',
                }}
              >
                <Typography
                  component="legend"
                  sx={{ padding: '0 8px', fontSize: '0.875rem', fontWeight: 500 }}
                  variant="subtitle2"
                >
                  Información del Sitio
                </Typography>
                <Stack spacing={2} sx={{ marginTop: 1 }}>
                  <Controller
                    control={form.control}
                    name="name"
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth error={!!fieldState.error} variant="outlined">
                        <TextField
                          {...field}
                          fullWidth
                          label="Nombre del sitio"
                          placeholder={namePlaceholder}
                          size="small"
                        />
                        <FormHelperText error={!!fieldState.error}>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                    rules={nameValidationRules}
                  />
                </Stack>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button disabled={isSubmitDisabled} type="submit" variant="contained">
              Agregar Sitio
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}

export default AddManualSiteFormModal
